<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <div class="page-header">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item :to="{ name: 'DashboardHome' }">
              <i class="fa fa-home"></i>
            </b-breadcrumb-item>
            <b-breadcrumb-item :to="{ name: 'PartnerMarketingEventList' }" :active="!fromEventPage">點數/商品兌換活動</b-breadcrumb-item>
            <b-breadcrumb-item v-if="fromEventPage && parentEvent" @click="backToEventList">{{ parentEvent.name }}</b-breadcrumb-item>
            <b-breadcrumb-item active>全部活動</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>
    </div>
    <b-card>
      <div class="d-flex flex-column flex-xl-row align-items-start align-items-xl-center justify-content-between mb-4 mb-xl-2">
        <h4 class="mb-2 mb-xl-0 font-weight-bold">{{ fromEventPage && parentEvent ? `${parentEvent.name} - 兌換活動方案` : '全部活動' }}</h4>
        <div class="col-12 col-xl-10 d-flex flex-column flex-xl-row align-items-start align-items-xl-center justify-content-xl-end">
          <b-button v-if="checkPermission([consts.PARTNER_MARKETING_PLAN_CREATE])" class="flex-shrink-0 mb-2 mb-xl-0" variant="primary" @click="goCreate">
            <i class="fa fa-plus"></i> 新增方案
          </b-button>
          <b-form-select class="ml-0 ml-xl-2 mb-2 mb-xl-0" style="max-width: 150px;" :value="partner_name" @input="updateFilter('partner_name', $event)" :options="partnerOptions" />
          <b-form-select class="ml-0 ml-xl-2 mb-2 mb-xl-0" style="max-width: 120px;" :value="is_processing" @input="updateFilter('is_processing', $event)" :options="isProcessingOptions" />
          <b-form-select class="ml-0 ml-xl-2 mb-2 mb-xl-0" style="max-width: 120px;" :value="status" @input="updateFilter('status', $event)" :options="statusOptions" />
          <b-input-group class="ml-0 ml-xl-4" style="max-width: 220px; flex: 1 1 0;">
            <b-form-input v-model="keyword" placeholder="請輸入活動名稱或方案名稱" @keyup.enter="handleSearch" />
            <b-input-group-append>
              <b-button @click="handleSearch"><i class="fa fa-search"></i></b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>
      <b-table
        striped
        hover
        responsive
        :items="list"
        :fields="fields"
        :busy="isLoading"
        show-empty
        sort-by="sort"
        :sort-desc="false"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
        <template #head(name)="data">
          {{ data.label }}
          <BIconQuestionCircle
            class="d-inline-block ml-1"
            id="plan-name-tooltip"
          ></BIconQuestionCircle>
          <b-popover
            target="plan-name-tooltip"
            triggers="hover focus"
          >
            <div class="text-left">
              【內部設定人員參考辨識用，前台畫面不顯示】
            </div>
          </b-popover>
        </template>
        <template #cell(partner_name)="data">
          {{ data.item.partner_marketing_event ? data.item.partner_marketing_event.name : '-' }}
        </template>
        <template #cell(image_url)="data">
          <b-img-lazy v-if="data.item.image_url" :src="data.item.image_url" style="width: 80px; height: 80px; object-fit: contain;" />
        </template>
        <template #cell(is_active)="data">
          <b-badge :variant="isActive(data.item) ? 'success' : 'warning'">
            {{ isActive(data.item) ? '上架' : '下架' }}
          </b-badge>
        </template>
        <template #cell(status)="data">
          <b-badge :variant="statusMap[data.item.status] ? statusMap[data.item.status].variant : 'secondary'">
            {{ statusMap[data.item.status] ? statusMap[data.item.status].text : data.item.status }}
          </b-badge>
        </template>
        <template #cell(start_at)="data">
          {{ formatDate(data.item.start_at) }}
        </template>
        <template #cell(end_at)="data">
          {{ formatDate(data.item.end_at) }}
        </template>
        <template #cell(actions)="data">
          <b-dropdown size="sm" variant="secondary">
            <template slot="button-content">
              <i class="mdi mdi-menu"></i>
            </template>
            <b-dropdown-item @click="goCodeManagement(data.item)">序號管理</b-dropdown-item>
            <b-dropdown-item @click="goCopy(data.item)">複製 (複製方案)</b-dropdown-item>
          </b-dropdown>
          <b-button size="sm" variant="inverse-primary" @click="goView(data.item)" class="ml-1"><span class="mdi mdi-eye mr-1"></span>查看</b-button>
          <b-button v-if="checkPermission([consts.PARTNER_MARKETING_PLAN_MODIFY])" size="sm" variant="inverse-warning" class="ml-1" @click="goEdit(data.item)"><span class="mdi mdi-lead-pencil mr-1"></span>編輯</b-button>
          <template v-if="checkPermission([consts.PARTNER_MARKETING_PLAN_DELETE])">
            <span :id="`delete-btn-${data.item.id}`">
              <b-button
                size="sm"
                variant="inverse-danger"
                class="ml-1"
                :disabled="data.item.status !== 'closed'"
                @click="remove(data.item)"
              ><span class="mdi mdi-delete mr-1"></span>刪除</b-button>
            </span>
            <b-tooltip v-if="data.item.status !== 'closed'" :target="`delete-btn-${data.item.id}`" placement="top">發布狀態為關閉時，才能刪除</b-tooltip>
          </template>
        </template>
      </b-table>
      <CustomPagination
        :currentPage="page"
        :totalRows="pagination.total"
        :perPage="per_page"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
      />
    </b-card>
  </div>
</template>

<script>
import partnerMarketingPlan from '@/apis/partnerMarketingPlan'
import partnerMarketingEvent from '@/apis/partnerMarketingEvent'
import { BBadge, BButton, BCard, BTable, BFormInput, BInputGroup, BInputGroupAppend, BImgLazy, BBreadcrumb, BBreadcrumbItem, BDropdown, BDropdownItem, BTooltip, BIconQuestionCircle, BPopover, BSpinner } from 'bootstrap-vue'
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue"
import { updateQueryFromRoute, updateUrlWithoutReload } from '@/utils/updateUrl'
import moment from 'moment'
import PermissionChecker from "@/utils/PermissionChecker"
import * as consts from "@/consts"

export default {
  components: { BBadge, BButton, BCard, BTable, BFormInput, BInputGroup, BInputGroupAppend, BImgLazy, BBreadcrumb, BBreadcrumbItem, BDropdown, BDropdownItem, BTooltip, BIconQuestionCircle, BPopover, BSpinner, CustomPagination },
  data() {
    return {
      consts,
      list: [],
      isLoading: false,
      initialized: false,
      partnerNames: [],
      keyword: '',
      pagination: {
        total: 0
      },
      statusOptions: [
        { value: null, text: '全部狀態' },
        { value: 'draft', text: '草稿' },
        { value: 'public', text: '公開' },
        { value: 'closed', text: '關閉' }
      ],
      isProcessingOptions: [
        { value: null, text: '全部資料' },
        { value: true, text: '生效中' },
        { value: false, text: '已失效' }
      ],
      statusMap: {
        public: { text: '公開', variant: 'success' },
        draft: { text: '草稿', variant: 'warning' },
        closed: { text: '關閉', variant: 'secondary' }
      },
      parentEvent: null
    }
  },
  computed: {
    fromEventPage() {
      return !!this.$route.params.partnerMarketingEventId;
    },
    // 從 URL 讀取 filter values
    status() {
      return this.$route.query.status || null
    },
    is_processing() {
      return this.$route.query.is_processing !== undefined ? this.$route.query.is_processing : null
    },
    partner_name() {
      return this.$route.query.partner_name || null
    },
    page() {
      return parseInt(this.$route.query.page, 10) || 1
    },
    per_page() {
      return parseInt(this.$route.query.per_page, 10) || 10
    },
    partnerOptions() {
      return [
        { value: null, text: '全部活動' },
        ...this.partnerNames.map(name => ({ value: name, text: name }))
      ];
    },
    fields() {
      const baseFields = [
        { key: 'partner_name', label: '活動名稱' },
        { key: 'name', label: '方案名稱', sortable: true },
        { key: 'source_points', label: 'POYA點數' },
        { key: 'target_points', label: '兌換數量' },
        { key: 'target_unit', label: '兌換單位' },
        { key: 'is_active', label: '上架狀態' },
        { key: 'status', label: '狀態' },
        { key: 'start_at', label: '上架時間', sortable: true },
        { key: 'end_at', label: '下架時間', sortable: true },
        { key: 'actions', label: '管理', class: 'text-nowrap' }
      ];

      // 只有在篩選特定活動或從 event 頁進入時，才顯示排序欄位
      if (this.fromEventPage || this.partner_name) {
        baseFields.push({ key: 'sort', label: '排序', sortable: true });
      }

      return baseFields;
    }
  },
  async created() {
    // 從 URL 同步 keyword（two-way binding 需要）
    this.keyword = this.$route.query.keyword || ''

    if (this.fromEventPage) {
      await this.fetchParentEvent();
    }
    await this.fetchPartnerNames();
    // 不要在這裡設置 initialized，讓 watcher 的 immediate 執行
  },
  methods: {
    formatDate(val) {
      return val ? moment(val).format('YYYY-MM-DD') : ''
    },
    async fetchPartnerNames() {
      try {
        const res = await partnerMarketingPlan.getPartnerNames()
        this.partnerNames = res.data.data || []
      } catch (error) {
        console.error('Failed to fetch partner names:', error)
      }
    },
    fetchList() {
      // 從 $route.query 讀取所有參數
      const page = this.page
      const per_page = this.per_page
      const keyword = this.$route.query.keyword || ''
      const status = this.status
      const is_processing = this.is_processing
      const partner_name = this.partner_name

      this.isLoading = true
      const params = {
        page,
        per_page,
        keyword,
        status,
        partner_name,
        sort_by: 'sort',
        order_by: 'asc',
      }

      // 只有在值不是 null 時才加入參數
      if (is_processing !== null) {
        params.is_processing = is_processing
      }

      const apiCall = this.fromEventPage
        ? partnerMarketingPlan.getPartnerMarketingPlansByEvent(this.$route.params.partnerMarketingEventId, params)
        : partnerMarketingPlan.getPartnerMarketingPlans(params);

      apiCall.then(res => {
        this.list = res.data.data
        this.pagination.total = res.data.meta ? parseInt(res.data.meta.total, 10) : 0
      }).finally(() => {
        this.isLoading = false
      })
    },
    updateFilter(key, value) {
      // 只更新 URL，重置 page 為 1
      this.$router.push({
        query: {
          ...this.$route.query,
          [key]: value,
          page: 1
        }
      })
    },
    goCreate() {
      if (this.fromEventPage) {
        this.$router.push({ 
          name: 'PartnerMarketingPlanCreateByEvent', 
          params: { partnerMarketingEventId: this.$route.params.partnerMarketingEventId }
        });
      } else {
        this.$router.push({ name: 'PartnerMarketingPlanCreate' });
      }
    },
    goView(row) {
      if (this.fromEventPage) {
        this.$router.push({ 
          name: 'PartnerMarketingPlanViewByEvent', 
          params: { 
            partnerMarketingEventId: this.$route.params.partnerMarketingEventId,
            partnerMarketingPlanId: row.id 
          }
        });
      } else {
        this.$router.push({ name: 'PartnerMarketingPlanView', params: { partnerMarketingPlanId: row.id } });
      }
    },
    goEdit(row) {
      if (this.fromEventPage) {
        this.$router.push({ 
          name: 'PartnerMarketingPlanEditByEvent', 
          params: { 
            partnerMarketingEventId: this.$route.params.partnerMarketingEventId,
            partnerMarketingPlanId: row.id 
          }
        });
      } else {
        this.$router.push({ name: 'PartnerMarketingPlanEdit', params: { partnerMarketingPlanId: row.id } });
      }
    },
    goCopy(row) {
      if (this.fromEventPage) {
        this.$router.push({ 
          name: 'PartnerMarketingPlanCreateByEvent', 
          params: { partnerMarketingEventId: this.$route.params.partnerMarketingEventId },
          query: { copyFromId: row.id }
        });
      } else {
        this.$router.push({ 
          name: 'PartnerMarketingPlanCreate',
          query: { copyFromId: row.id }
        });
      }
    },
    goCodeManagement(row) {
      if (this.fromEventPage) {
        this.$router.push({ 
          name: 'PartnerMarketingPlanCodeListByEvent', 
          params: { 
            partnerMarketingEventId: this.$route.params.partnerMarketingEventId,
            planId: row.id 
          }
        });
      } else {
        this.$router.push({ 
          name: 'PartnerMarketingPlanCodeList', 
          params: { planId: row.id } 
        });
      }
    },
    async remove(row) {
      try {
        // 檢查方案狀態：「已關閉」或「已失效」
        const isClosed = row.status === 'closed';
        const isExpired = !this.isActive(row);

        // 只有在「非關閉」且「非已失效」狀態下，才檢查序號
        if (!isClosed && !isExpired) {
          // 先檢查是否有關聯序號
          const checkResponse = await partnerMarketingPlan.checkCodes(row.id);

          if (checkResponse.data.has_codes) {
            // 如果有序號，直接顯示錯誤提示
            this.$swal.fire({
              type: 'error',
              title: '無法刪除',
              text: '該方案已存在序號，無法進行刪除'
            });
            return;
          }
        }

        // 顯示刪除確認
        this.$swal.fire({
          type: 'warning',
          title: '你確定要刪除嗎？',
          html: `
            <div class="my-3">
              <div>方案名稱：${row.name}</div>
            </div>`,
          confirmButtonText: '確定刪除',
          cancelButtonText: '不刪除',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          reverseButtons: true
        }).then((result) => {
          if (result.value) {
            partnerMarketingPlan.deletePartnerMarketingPlan(row.id).then(() => {
              this.$swal.fire({
                type: 'success',
                text: `成功刪除 ${row.name}`
              })
              this.fetchList()
            }).catch(error => {
              // 以防萬一的錯誤處理（race condition）
              if (error.response && error.response.status === 422) {
                const message = error.response.data.message || '刪除失敗'
                this.$swal.fire({
                  type: 'error',
                  title: '無法刪除',
                  text: message
                })
              } else {
                this.$swal.fire({
                  type: 'error',
                  title: '刪除失敗',
                  text: '發生錯誤，請稍後再試'
                })
              }
            })
          }
        })
      } catch (error) {
        console.error('檢查序號時發生錯誤：', error);
        this.$swal.fire({
          type: 'error',
          title: '錯誤',
          text: '無法檢查方案狀態，請稍後再試'
        });
      }
    },
    isActive(item) {
      const now = new Date();
      const startAt = new Date(item.start_at);
      const endAt = new Date(item.end_at);
      return item.status === 'public' && startAt <= now && endAt >= now;
    },
    async fetchParentEvent() {
      try {
        const response = await partnerMarketingEvent.getPartnerMarketingEvent(this.$route.params.partnerMarketingEventId);
        this.parentEvent = response.data.data;
      } catch (error) {
        console.error('Failed to fetch parent event:', error);
      }
    },
    backToEventList() {
      this.$router.push({ name: 'PartnerMarketingEventList' });
    },
    handlePageChange(page) {
      // 只更新 URL
      this.$router.push({
        query: {
          ...this.$route.query,
          page
        }
      })
    },
    handlePerPageChange(perPage) {
      // 只更新 URL，重置 page 為 1
      this.$router.push({
        query: {
          ...this.$route.query,
          per_page: perPage,
          page: 1
        }
      })
    },
    handleSearch() {
      // 只更新 URL，加入 keyword
      this.$router.push({
        query: {
          ...this.$route.query,
          keyword: this.keyword,
          page: 1
        }
      })
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    }
  },
  watch: {
    // 統一由 $route.query 處理所有變化
    '$route.query': {
      handler() {
        // 同步 keyword（two-way binding）
        this.keyword = this.$route.query.keyword || ''

        // 重新載入資料
        this.fetchList()

        // 第一次執行後設置 initialized
        if (!this.initialized) {
          this.initialized = true
        }
      },
      deep: true,
      immediate: true
    }
  }
}
</script>